import { getTokenCookies, setTokenCookies } from 'utils/cookie'

export type TruckItemType = {
  truck_id: number
  truck_name: string
  truck_plate: string
  truck_status: string
  origin_location_id: number
  origin_location_name: string
  sla: string
  order_delivery_at: number
  truck_lat: number
  truck_lon: number
}

export type TruckPositionType = {
  id: number
  truck_name: string
  truck_status: string
  lat: number
  lon: number
  timestamp: number
}

export type UserDataTruck = {
  userId: number
  username: string
  auth: string
  locations: {
    id: number
    latitude: string
    longitude: string
  }[]
}

export type LiveTrackingTruckSlice = {
  isAuthorized: boolean
  isLoading: boolean
  userData: UserDataTruck | null
  truckPosition: Record<string, TruckPositionType>
  listTruckActive: TruckItemType[]
  truckSelected: TruckItemType | null
  setIsLoading: (isLoading: boolean) => void
  setUserData: (payload: UserDataTruck) => void
  setTruckPosition: (
    payload: TruckPositionType,
    callback: (truckHasAdded: boolean, allTruckPositions: [number, number][]) => void,
  ) => void
  setTruckSelected: (payload: TruckItemType | null, callback?: () => void) => void
  setUpdateTruck: (payload: TruckItemType[]) => void
}

const token = getTokenCookies()

export const createLiveTrackingTruckSlice: StoreSlice<LiveTrackingTruckSlice> = (set, get) => ({
  isAuthorized: !!token,
  isLoading: true,
  userData: null,
  truckPosition: {},
  listTruckActive: [],
  truckSelected: null,
  setIsLoading: (isLoading) => {
    set((state) => {
      state.isLoading = isLoading
    })
  },
  setUserData: (payload) => {
    set((state) => {
      state.userData = payload
    })
    setTokenCookies(payload.auth)
  },
  setTruckPosition: (payload, callback) => {
    const { id } = payload
    const { truckPosition } = get()

    const allTruckPositions: [number, number][] = []

    Object.values(truckPosition).forEach((data) => {
      allTruckPositions.push([data.lat, data.lon])
    })
    allTruckPositions.push([payload.lat, payload.lon])

    set((state) => {
      state.truckPosition[id] = payload
    })

    if (callback) callback(!!truckPosition[id], allTruckPositions)
  },
  setTruckSelected: (payload, callback) => {
    set((state) => {
      state.truckSelected = payload
    })

    if (callback) callback()
  },
  setUpdateTruck: (payload) => {
    const { truckSelected } = get()
    const incomingIds = new Set(payload.map((item) => String(item.truck_id)))

    set((state) => {
      state.listTruckActive = payload

      // remove markers for trucks not in the filtered payload
      Object.keys(state.truckPosition).forEach((key) => {
        if (!incomingIds.has(key)) {
          delete state.truckPosition[key]
        }
      })

      // add new entries (from mock/REST) or update existing ones (from MQTT)
      payload.forEach((item) => {
        const existing = state.truckPosition[item.truck_id]

        if (!existing && item.truck_lat && item.truck_lon) {
          // seed position from REST data (no MQTT yet)
          state.truckPosition[item.truck_id] = {
            id: item.truck_id,
            truck_name: item.truck_name,
            truck_status: item.truck_status,
            lat: item.truck_lat,
            lon: item.truck_lon,
            timestamp: Date.now(),
          }
        } else if (existing) {
          // keep MQTT-sourced position, only update status/name
          state.truckPosition[item.truck_id].truck_status = item.truck_status
          state.truckPosition[item.truck_id].truck_name = item.truck_name
        }
      })

      if (truckSelected) {
        const updated = payload.find((item) => item.truck_id === truckSelected.truck_id)
        if (updated) state.truckSelected = updated
      }
    })

    set((state) => {
      state.isLoading = false
    })
  },
})
